import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    FormFeedback,
    ModalHeader,
} from "reactstrap";
import {useContext, useState} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {toast} from "react-toastify";
import Loader from "../../components/Loader";
import AuthContext from "../../context/AuthContext";
import handleUnauthorized401MutateError from "../../utils/handleUnauthorized401MutateError";
import {addProducts, getMerchantKYCIndiaInfo} from "../../api/api";


const AddProductButton = () => {
    const {refreshToken} = useContext(AuthContext)
    const [addProductModal, setAddProductModal] = useState(false);
    const openAddProductModal = () => setAddProductModal(true);
    const closeAddProductModal = () => setAddProductModal(false);
    const toggleAddProductModal = () => setAddProductModal((prevState) => !prevState);
    const [productName, setProductName] = useState('');
    const [productPrice, setProductPrice] = useState('');
    const [productDescription, setProductDescription] = useState('');
    const [productIsDigital, setProductIsDigital] = useState(false);
    const [productIsInventoryTrackingEnabled, setProductIsInventoryTrackingEnabled] = useState(false);
    const [productStock, setProductStock] = useState('');
    const [productImage, setProductImage] = useState('');
    const [productUPC, setProductUPC] = useState('');
    const [productHSNSAC, setProductHSNSAC] = useState('');
    const [productUnitOfMeasure, setProductUnitOfMeasure] = useState('');
    const [productTaxType, setProductTaxType] = useState('');
    const [productTaxPercentage,setProductTaxPercentage] = useState('');
    const [advancedOptions, setAdvancedOptions] = useState(false);

    const queryClient = useQueryClient()

    const { data: kycData } = useQuery({
        queryKey: ['merchant-kyc-india'],
        queryFn: getMerchantKYCIndiaInfo,
    });
    const canChargeGst = kycData?.results?.[0]?.can_charge_gst ?? true;

    const addProductMutation = useMutation({
        mutationFn: addProducts,
        onSuccess: (data, variables) =>{
            closeAddProductModal()
            queryClient.invalidateQueries(['products'])
            setProductName('')
            setProductPrice('')
            setProductDescription('')
            setProductUPC('')
            setProductHSNSAC('')
            setProductUnitOfMeasure('')
            setProductTaxType('')
            setProductTaxPercentage('')
            setProductIsDigital(false)
            setProductIsInventoryTrackingEnabled(false)
            setProductStock('')
            setProductImage('')
            setAdvancedOptions(false);
            toast.success('Product Added!')
        },
        onError: (err, variables) => {
            handleUnauthorized401MutateError(err, refreshToken, addProductMutation, variables)
            if(err.response.status !== 401){
                toast.error(`An error occurred! ${err?.response?.data?.detail}`)
            }
        }
    })


    const handleAddProductFormSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append("name", productName);
        formData.append("price", productPrice);
        formData.append("description", productDescription);
        formData.append("upc", productUPC);
        formData.append("hsn_sac", productHSNSAC);
        formData.append("unit_of_measure", productUnitOfMeasure);
        formData.append("tax_type", productTaxType);
        formData.append("tax_percentage", productTaxPercentage);
        formData.append("is_digital", productIsDigital);
        formData.append("is_inventory_tracking_enabled", productIsInventoryTrackingEnabled)
        formData.append("stock", productStock);
        formData.append("is_advanced_options", advancedOptions);
        if (productImage) formData.append("image", productImage);

        addProductMutation.mutate(formData)
    };

    return (
        <>
            <div className="btn bx-btn-primary waves-effect waves-light mb-3" onClick={openAddProductModal}>
                <span><i className="mdi mdi-plus-circle mr-1"/>Add Product</span>
            </div>

            <Modal isOpen={addProductModal} toggle={toggleAddProductModal} backdrop="static" keyboard={false}>
                <ModalHeader toggle={toggleAddProductModal} close={
                    <button type="button" className="close" onClick={closeAddProductModal}>
                        &times;
                    </button>
                }>Add Product</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleAddProductFormSubmit}>
                        <FormGroup>
                            <Label for="name">Name<span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                name="name"
                                id="productName"
                                placeholder="Product name"
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                                // invalid={true}

                                invalid={
                                    addProductMutation.isError &&
                                    addProductMutation.error.response.data.name
                                }
                            />
                            {addProductMutation.isError && addProductMutation.error.response.data.name && (
                                <FormFeedback invalid>
                                    {addProductMutation.error.response.data.name}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label for="price">Price<span className="text-danger">*</span></Label>
                            <Input
                                type="text"
                                min="1"
                                step="any"
                                name="price"
                                id="Price"
                                placeholder="Product Price"
                                value={productPrice}
                                onChange={(e) => setProductPrice(e.target.value)}
                                invalid={
                                    addProductMutation.isError &&
                                    addProductMutation.error.response.data.price
                                }
                            />
                            {addProductMutation.isError && addProductMutation.error.response.data.price && (
                                <FormFeedback invalid>
                                    {addProductMutation.error.response.data.price}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <FormGroup className="form-group mb-3">
                            <div className="custom-control custom-checkbox">
                                <Input
                                    type="checkbox"
                                    className="custom-control-input"
                                    id="checkbox-advanced-options"
                                    checked={advancedOptions}
                                    onChange={() => setAdvancedOptions(!advancedOptions)}
                                />
                                <Label
                                    className="custom-control-label"
                                    for="checkbox-advanced-options"
                                >
                                    Advanced Options
                                </Label>
                            </div>
                        </FormGroup>
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="description">Description</Label>
                                <Input
                                    type="textarea"
                                    name="Description"
                                    id="productDescription"
                                    placeholder="Product Description"
                                    value={productDescription}
                                    onChange={(e) => setProductDescription(e.target.value)}
                                    invalid={
                                        addProductMutation.isError &&
                                        addProductMutation.error.response.data.description
                                    }
                                />
                                {addProductMutation.isError && addProductMutation.error.response.data.description && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.description}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="UPC">UPC</Label>
                                <Input
                                    type="text"
                                    name="UPC"
                                    id="productUPC"
                                    value={productUPC}
                                    placeholder="Product UPC"
                                    onChange={(e) => setProductUPC(e.target.value)}
                                    invalid={
                                        addProductMutation.isError &&
                                        addProductMutation.error.response.data.upc
                                    }
                                />
                                {addProductMutation.isError && addProductMutation.error.response.data.upc && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.upc}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                            )}
                        {advancedOptions && canChargeGst && (
                            <FormGroup>
                                <Label for="HSN SAC">HSN/SAC</Label>
                                <Input
                                    type="text"
                                    name="HSN SAC"
                                    id="productHSNSAC"
                                    value={productHSNSAC}
                                    placeholder="Product HSN/SAC"
                                    onChange={(e) => setProductHSNSAC(e.target.value)}
                                    invalid={
                                        addProductMutation.isError &&
                                        addProductMutation.error.response.data.hsn_sac
                                    }
                                />
                                {addProductMutation.isError && addProductMutation.error.response.data.hsn_sac && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.hsn_sac}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup>
                                <Label for="Unit of Measure">Unit of Measure</Label>
                                <Input
                                    type="text"
                                    name="Unit of Measure"
                                    id="Unit of Measure"
                                    value={productUnitOfMeasure}
                                    placeholder="Unit of Measure"
                                    onChange={(e) => setProductUnitOfMeasure(e.target.value)}
                                    invalid={
                                        addProductMutation.isError &&
                                        addProductMutation.error.response.data.unit_of_measure
                                    }
                                />
                                {addProductMutation.isError && addProductMutation.error.response.data.unit_of_measure && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.unit_of_measure}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions&& (
                            <FormGroup className="form-group mb-3">
                                <div className="custom-control custom-checkbox">
                                    <Input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="checkbox-digital"
                                        checked={productIsDigital}
                                        onChange={() => setProductIsDigital(!productIsDigital)}
                                        invalid={
                                            addProductMutation.isError &&
                                            addProductMutation.error.response.data.is_digital
                                        }
                                    />
                                    <label
                                        className="custom-control-label"
                                        for="checkbox-digital"
                                    >
                                        Digital Product
                                    </label>
                                </div>
                                {addProductMutation.isError && addProductMutation.error.response.data.is_digital && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.is_digital}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions && canChargeGst && (
                             <FormGroup>
                                <Label for="tax_type">Tax Type</Label>
                                <Input
                                    type="select"
                                    id="inputState"
                                    className="form-control mb-2"
                                    value={productTaxType}
                                    onChange={(e) => setProductTaxType(e.target.value)}
                                >
                                    <option value="">--</option>
                                    <option value={"TAX-INCLUSIVE"}>Tax Inclusive</option>
                                    <option value={"TAX-EXCLUSIVE"}>Tax Exclusive</option>
                                </Input>
                                {addProductMutation.isError && addProductMutation.error.response.data.tax_type && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.tax_type}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                        )}
                        {advancedOptions && canChargeGst && (
                            <FormGroup>
                                <Label for="tax_percentage">Tax Percentage</Label>
                                <Input
                                    type="text"
                                    name="tax_percentage"
                                    id="productTaxPercentage"
                                    value={productTaxPercentage}
                                    placeholder="Tax Percentage"
                                    onChange={(e) => setProductTaxPercentage(e.target.value)}
                                    invalid={
                                        addProductMutation.isError &&
                                        addProductMutation.error.response.data.tax_percentage
                                    }
                                />
                                {addProductMutation.isError && addProductMutation.error.response.data.tax_percentage && (
                                    <FormFeedback invalid>
                                        {addProductMutation.error.response.data.tax_percentage}
                                    </FormFeedback>
                                )}
                            </FormGroup>
                            )}
                        {advancedOptions && !canChargeGst && (
                            <div className="alert alert-info mb-3">
                                <small>GST tax fields are disabled because your business is not registered as a regular GST dealer.</small>
                            </div>
                        )}
                        {advancedOptions&& (
                            <FormGroup className="form-group mb-3">
                                <div className="custom-control custom-checkbox">
                                    <Input
                                        type="checkbox"
                                        className="custom-control-input"
                                        id="checkbox-inventory-tracking"
                                        checked={productIsInventoryTrackingEnabled}
                                        onChange={() => setProductIsInventoryTrackingEnabled(!productIsInventoryTrackingEnabled)}
                                        invalid={
                                            addProductMutation.isError &&
                                            addProductMutation.error.response.data.is_inventory_tracking_enabled
                                        }
                                    />
                                    <Label
                                        className="custom-control-label"
                                        for="checkbox-inventory-tracking"
                                    >
                                        Inventory Tracking
                                    </Label>
                                    {addProductMutation.isError && addProductMutation.error.response.data.is_inventory_tracking_enabled && (
                                        <FormFeedback invalid>
                                            {addProductMutation.error.response.data.is_inventory_tracking_enabled}
                                        </FormFeedback>
                                    )}
                                </div>
                            </FormGroup>
                        )}
                        {productIsInventoryTrackingEnabled&& advancedOptions&& (
                        <FormGroup>
                            <Label for="stock">Stock</Label>
                            <Input
                                type="text"
                                // min={checked ? "0" : "1"}
                                name="stock"
                                id="stock"
                                placeholder="Product Initial Stock Value"
                                value={productStock}
                                onChange={(e) => setProductStock(e.target.value)}
                                invalid={
                                    addProductMutation.isError &&
                                    addProductMutation.error.response.data.stock
                                }
                            />
                            {addProductMutation.isError && addProductMutation.error.response.data.stock && (
                                <FormFeedback invalid>
                                    {addProductMutation.error.response.data.stock}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        )}
                        <FormGroup>
                            <Label for="image">Image</Label>
                            <Input
                                type="file"
                                name="productImage"
                                id="productImage"
                                onChange={(e) => setProductImage(e.target.files[0])}
                                invalid={
                                    addProductMutation.isError &&
                                    addProductMutation.error.response.data.image
                                }
                            />
                            {addProductMutation.isError && addProductMutation.error.response.data.image && (
                                <FormFeedback invalid>
                                    {addProductMutation.error.response.data.image}
                                </FormFeedback>
                            )}
                        </FormGroup>
                        <Button
                            color="#4d5bed"
                            className="bx-btn-primary"
                            block
                            disabled={addProductMutation.isLoading}
                        >
                            {addProductMutation.isLoading? <><Loader size={'small'}/> Adding Product...</>: `Add Product`}

                        </Button>
                    </Form>
                </ModalBody>
            </Modal>
        </>
    );
};

export default AddProductButton;
